/**
 * Backend error -> AuthErrorKind + user-facing copy. Ported from HexagieApp
 * `src/shared/services/authErrors.ts`.
 *
 * Auth-flow screens must read failures through `parseAuthError`, NOT through
 * the generic `getApiErrorMessage`. The session endpoints reuse generic codes
 * that only mean something in context: `not.found` is an expired session, and
 * a bare 403 is an OTP request lockout.
 */

import { OTP_REQUEST_WINDOW_HOURS } from "./authPolicy";

/**
 * @typedef {"OTP_INVALID"|"OTP_EXPIRED"|"SESSION_EXPIRED"|"OTP_LIMIT"|"ACCOUNT_BLOCKED"|"ACCOUNT_DEACTIVATED"|"INVALID_CONTACT"|"FORBIDDEN"|"NETWORK"|"UNKNOWN"} AuthErrorKind
 */

/**
 * @typedef {Object} AuthError
 * @property {AuthErrorKind} kind
 * @property {string} message      Canned per-kind copy.
 * @property {string|null} reason  The server's own sentence, when it sent one.
 * @property {number|null} retryAfterSeconds
 * @property {boolean} recoverable Can the user fix it without starting over?
 */

const MESSAGES = {
  OTP_INVALID: "Incorrect code. Please check and try again.",
  OTP_EXPIRED: "That code has expired. Tap resend to get a new one.",
  SESSION_EXPIRED: "Your session expired. Please start again.",
  OTP_LIMIT: `Too many code requests. Try again in up to ${OTP_REQUEST_WINDOW_HOURS} hours.`,
  ACCOUNT_BLOCKED: "This account is blocked. Please contact support.",
  ACCOUNT_DEACTIVATED:
    "This account is deactivated. Verify your contacts to restore it.",
  INVALID_CONTACT: "That contact does not look valid. Please check it.",
  FORBIDDEN: "You are not allowed to do that.",
  NETWORK: "No connection. Check your internet and try again.",
  UNKNOWN: "Something went wrong. Please try again.",
};

const RECOVERABLE = ["OTP_INVALID", "OTP_EXPIRED", "NETWORK"];

const DEACTIVATED_CODES = [
  "account.deactivated",
  "account.disabled",
  "consumer.deactivated",
  "consumer.disabled",
  "identity.deactivated",
  "identity.disabled",
];

const DEACTIVATED_TEXT = ["not active", "inactive", "deactivat", "disabled"];

const readCode = (error) =>
  String(error?.response?.data?.errorCode ?? "").toLowerCase();

const readAllText = (error) =>
  [
    error?.response?.data?.reason,
    error?.response?.data?.message,
    error?.response?.data?.error,
  ]
    .filter((value) => typeof value === "string")
    .join(" ")
    .toLowerCase();

export const readServerReason = (error) => {
  const reason = error?.response?.data?.reason;
  if (typeof reason !== "string") return null;

  const trimmed = reason.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const readRetryAfter = (error) => {
  const fromBody = error?.response?.data?.retryAfterSeconds;
  if (typeof fromBody === "number" && Number.isFinite(fromBody)) return fromBody;

  const parsed = Number(error?.response?.headers?.["retry-after"]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const classify = (error) => {
  if (!error?.response) return "NETWORK";

  const status = error.response.status;
  const code = readCode(error);

  /**
   * Matches across reason, message AND error rather than `message` alone.
   *
   * Verified on dev 2026-09-16: `POST /otp/verify/` with a dead traceId
   * answers 404 with `message: "Resource not found"` and the real
   * information in `reason: "OTP expired"`. `message` is NOT reliably null,
   * and when it is populated it shadows the useful text — so reading it
   * first classified an expired code as an expired SESSION, which is fatal
   * and resets the user to Get Started instead of offering resend.
   *
   * This is a deliberate divergence from the app, which has the same bug.
   */
  const text = readAllText(error);

  if (code === "account.blocked") return "ACCOUNT_BLOCKED";
  if (DEACTIVATED_CODES.includes(code)) return "ACCOUNT_DEACTIVATED";
  if (code === "invalid.otp" || code === "otp.invalid") return "OTP_INVALID";
  if (code === "otp.expired") return "OTP_EXPIRED";
  if (code === "session.expired") return "SESSION_EXPIRED";

  if (
    code === "otp.attempts.exceeded" ||
    code === "otp.resend.cooldown" ||
    code === "too.many.requests"
  ) {
    return "OTP_LIMIT";
  }

  if (code === "invalid.identity.type" || code === "invalid.identity") {
    return "INVALID_CONTACT";
  }

  if (DEACTIVATED_TEXT.some((phrase) => readAllText(error).includes(phrase))) {
    return "ACCOUNT_DEACTIVATED";
  }

  if (status === 404) {
    return text.includes("otp") ? "OTP_EXPIRED" : "SESSION_EXPIRED";
  }

  if (status === 400 && code === "not.found") return "SESSION_EXPIRED";
  if (status === 400 && text.includes("session")) return "SESSION_EXPIRED";
  if (status === 400 && text.includes("otp")) return "OTP_INVALID";
  if (status === 429) return "OTP_LIMIT";
  if (status === 403) return "OTP_LIMIT";

  return "UNKNOWN";
};

/**
 * @param {any} error
 * @returns {AuthError}
 */
export const parseAuthError = (error) => {
  const kind = classify(error);

  return {
    kind,
    message: MESSAGES[kind],
    reason: readServerReason(error),
    retryAfterSeconds: readRetryAfter(error),
    recoverable: RECOVERABLE.includes(kind),
  };
};

/**
 * Get Started and SignUp prefer the server's wording over the canned copy,
 * because the cases that matter most to the user classify as UNKNOWN: a
 * duplicate identity is a 409 (no rule for it), and a deactivated account
 * answers 400 not.allowed with reason "Identity is not active". The canned
 * "Something went wrong" hides the one thing the user needs to know.
 *
 * Verification deliberately keeps the canned copy, shown inline.
 *
 * @param {AuthError} authError
 * @returns {string}
 */
export const preferServerReason = (authError) =>
  authError.reason ?? authError.message;

export default parseAuthError;
